/**
 * Language Selector Component - YUL ON TAP
 * 
 * Phase 3: Bilingual support (EN/FR)
 * Compact language toggle for header integration
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Globe } from 'lucide-react';

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
];

export default function LanguageSelector({ variant = 'default' }) {
  const { i18n } = useTranslation();
  
  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('yul_language', langCode);
  };

  // Compact variant for headers
  if (variant === 'compact') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            data-testid="language-selector"
            className="text-[hsl(215,20%,65%)] hover:text-white hover:bg-[hsl(217,19%,17%)] gap-2"
          >
            <Globe className="w-4 h-4" />
            <span className="uppercase text-xs font-medium">{currentLang.code}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]"
        >
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              data-testid={`lang-${lang.code}`}
              onClick={() => handleLanguageChange(lang.code)}
              className={`cursor-pointer ${
                i18n.language === lang.code 
                  ? 'text-blue-400 bg-blue-500/10' 
                  : 'text-white hover:bg-[hsl(217,19%,17%)]'
              }`}
            >
              <span className="mr-2">{lang.flag}</span>
              {lang.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default variant - larger for settings pages
  return (
    <div className="flex items-center space-x-2">
      {languages.map((lang) => (
        <Button
          key={lang.code}
          variant={i18n.language === lang.code ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleLanguageChange(lang.code)}
          className={
            i18n.language === lang.code
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'border-[hsl(217,19%,27%)] text-white hover:bg-[hsl(217,19%,17%)]'
          }
        >
          <span className="mr-2">{lang.flag}</span>
          {lang.name}
        </Button>
      ))}
    </div>
  );
}
